import { m } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { optimizedViewport } from "@/lib/motion";
import vertikalniLisyImg from "@/assets/vertikalni-lisy.webp";
import horizontalniLisyImg from "@/assets/horizontalni-lisy.webp";
import kompaktoryImg from "@/assets/kompaktory.webp";

// Image sizes for responsive loading - these are above the fold on most screens
const SERVICE_IMAGE_SIZES = "(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw";

const Services = () => {
  const { t } = useTranslation();
  const { localePath } = useLanguage();

  const services = [
    {
      title: t("services.vertical.title"),
      description: t("services.vertical.description"),
      image: vertikalniLisyImg,
      features: t("services.vertical.features", { returnObjects: true }) as string[],
      link: localePath("/produkty") + "?kategorie=vertikalni",
    },
    {
      title: t("services.horizontal.title"),
      description: t("services.horizontal.description"),
      image: horizontalniLisyImg,
      features: t("services.horizontal.features", { returnObjects: true }) as string[],
      link: localePath("/produkty") + "?kategorie=horizontalni",
    },
    {
      title: t("services.compactors.title"),
      description: t("services.compactors.description"),
      image: kompaktoryImg,
      features: t("services.compactors.features", { returnObjects: true }) as string[],
      link: localePath("/produkty"),
    },
  ];

  return (
    <section id="services" className="py-24 md:py-32 bg-card">
      <div className="container mx-auto px-4 sm:px-6">
        <m.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={optimizedViewport} transition={{ duration: 0.6 }} className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-sm font-medium text-primary mb-4 block">{t("services.badge")}</span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
            {t("services.title")} <span className="text-gradient">{t("services.titleHighlight")}</span> {t("services.titleEnd")}
          </h2>
          <p className="text-muted-foreground text-lg">{t("services.subtitle")}</p>
        </m.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <m.div key={service.title} initial={{ y: 30 }} whileInView={{ y: 0 }} viewport={optimizedViewport} transition={{ duration: 0.5, delay: index * 0.1 }} className="group rounded-2xl overflow-hidden bg-background border border-border hover:border-primary/30 transition-all duration-300">
              <div className="relative h-56 overflow-hidden">
                <picture>
                  <source srcSet={service.image} type="image/webp" />
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 [-webkit-backface-visibility:hidden] [backface-visibility:hidden]"
                    loading={index === 0 ? "eager" : "lazy"}
                    decoding="async"
                    sizes={SERVICE_IMAGE_SIZES}
                    width="400"
                    height="224"
                  />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent pointer-events-none" />
              </div>
              <div className="p-6">
                <h3 className="font-heading text-2xl font-semibold mb-3">{service.title}</h3>
                <p className="text-muted-foreground mb-4">{service.description}</p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={service.link}
                  aria-label={`${t("services.moreInfo")} ${service.title}`}
                >
                  <Button variant="outline" className="w-full group-hover:border-primary group-hover:text-primary">
                    {`${t("services.moreInfo")}: ${service.title}`}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </m.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
